import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import PageHeader from '../components/PageHeader';
import OrderMonthGroups from '../components/OrderMonthGroups';
import PendingVerificationTable from '../components/PendingVerificationTable';
import OrdersToolbar from '../components/OrdersToolbar';
import ReportExportBar from '../components/ReportExportBar';
import ConfirmDialog from '../components/ConfirmDialog';
import { filterOrders, sortOrders, groupOrdersByMonth } from '../utils/orderUtils';
import { advanceOrderStatus } from '../utils/orderActions';
import { runOrdersReportExport } from '../utils/reportExports';
import { STATUS_WORKFLOW } from '../utils/constants';
import { isOnlinePayment } from '../utils/paymentUtils';

/* ─────────────────────────────────────────────
   Section definitions
   ───────────────────────────────────────────── */

/**
 * Section 1 — Pending Payment Verification
 * Online payment orders that have NOT been verified yet.
 * These must NOT appear in Active Orders.
 */
const isPendingVerification = (o) =>
  isOnlinePayment(o) &&
  (o.paymentStatus === 'Pending Verification' || o.paymentStatus === 'Rejected');

/**
 * Section 2 — Active Orders
 * - All COD orders (status Pending / Confirmed)
 * - Online orders whose payment has been Verified (Confirmed / legacy Processing)
 * These are ready for fulfillment.
 */
const isActiveOrder = (o) => {
  const active = ['Pending', 'Confirmed', 'Processing', 'Packed'];
  if (!active.includes(o.status)) return false;
  if (isOnlinePayment(o)) {
    return o.paymentStatus === 'Verified';
  }
  return true; // COD — always active once order exists
};

/** Section 3 — Completed Orders */
const isCompleted = (o) => o.status === 'Dispatched' || o.status === 'Delivered';

/** Section 4 — Cancelled / Rejected */
const isCancelledOrRejected = (o) =>
  o.status === 'Cancelled' ||
  o.status === 'Payment Failed' ||
  o.status === 'Returned' ||
  o.paymentStatus === 'Rejected';

/* ─────────────────────────────────────────────
   Tab config
   ───────────────────────────────────────────── */
const TABS = [
  {
    id: 'verification',
    label: 'Pending Verification',
    icon: '⏳',
    filter: isPendingVerification,
    emptyMessage: 'No orders awaiting payment verification',
  },
  {
    id: 'active',
    label: 'Active Orders',
    icon: '📦',
    filter: isActiveOrder,
    emptyMessage: 'No active orders',
  },
  {
    id: 'completed',
    label: 'Completed',
    icon: '✓',
    filter: isCompleted,
    emptyMessage: 'No completed orders',
  },
  {
    id: 'cancelled',
    label: 'Cancelled / Rejected',
    icon: '✕',
    filter: isCancelledOrRejected,
    emptyMessage: 'No cancelled or rejected orders',
  },
];

/* ─────────────────────────────────────────────
   Component
   ───────────────────────────────────────────── */
export default function Orders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab = TABS.find((t) => t.id === rawTab)?.id ?? 'verification';

  const [allOrders, setAllOrders]         = useState([]);
  const [error, setError]                 = useState('');
  const [cancelId, setCancelId]           = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // orderId being actioned
  const [search, setSearch]               = useState('');
  const [dateFilter, setDateFilter]       = useState('');
  const [customFrom, setCustomFrom]       = useState('');
  const [customTo, setCustomTo]           = useState('');
  const [sortBy, setSortBy]               = useState('newest');

  /* ── Data loading ── */
  const load = useCallback(() => {
    api.getOrders()
      .then((orders) => setAllOrders(Array.isArray(orders) ? orders : []))
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 8000);
    return () => clearInterval(interval);
  }, [load]);

  /* ── Tab counts (unfiltered) ── */
  const tabCounts = useMemo(
    () => Object.fromEntries(TABS.map((t) => [t.id, allOrders.filter(t.filter).length])),
    [allOrders],
  );

  /* ── Orders for the active tab, with search/date/sort applied ── */
  const currentTabConfig = TABS.find((t) => t.id === activeTab);
  const tabOrders = useMemo(
    () => allOrders.filter(currentTabConfig.filter),
    [allOrders, currentTabConfig],
  );
  const filtered = useMemo(
    () => sortOrders(filterOrders(tabOrders, { search, dateFilter, customFrom, customTo }), sortBy),
    [tabOrders, search, dateFilter, customFrom, customTo, sortBy],
  );
  const groups = useMemo(() => groupOrdersByMonth(filtered), [filtered]);

  /* ── Tab switching ── */
  const setTab = (id) => {
    setSearchParams(id === 'verification' ? {} : { tab: id });
    setSearch('');
    setDateFilter('');
    setCustomFrom('');
    setCustomTo('');
  };

  /* ── Fulfillment actions (Active Orders tab) ── */
  const handleAdvance = (orderId, status) => {
    if (!status) return;
    setError('');
    const order = allOrders.find((o) => o.orderId === orderId);
    advanceOrderStatus(api, orderId, status, order)
      .then(load)
      .catch((e) => setError(e.message));
  };

  const handleCancel = () => {
    const id = cancelId;
    if (!id) return;
    setError('');
    api.cancelOrder(id)
      .then(load)
      .catch((e) => setError(e.message))
      .finally(() => setCancelId(null));
  };

  /* ── Payment verification actions (Pending Verification tab) ── */
  const handleVerify = (orderId) => {
    setError('');
    setActionLoading(orderId);
    api.verifyPayment(orderId)
      .then(load)
      .catch((e) => setError(e.message))
      .finally(() => setActionLoading(null));
  };

  const handleReject = (orderId) => {
    setError('');
    setActionLoading(orderId);
    api.rejectPayment(orderId)
      .then(load)
      .catch((e) => setError(e.message))
      .finally(() => setActionLoading(null));
  };

  const nextStatus = (current) => STATUS_WORKFLOW[current];

  /* ── Active Orders: show Cancel only for Pending COD orders ── */
  const canCancel = (order) =>
    order.status === 'Pending' && !isOnlinePayment(order);

  /* ── Render ── */
  return (
    <div>
      <PageHeader
        title="Orders"
        subtitle="Manage payment verification and order fulfillment"
      />

      {/* ── Tab bar ── */}
      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setTab(tab.id)}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
              activeTab === tab.id
                ? 'bg-lumora-wood text-lumora-white shadow-glow'
                : 'bg-lumora-white/60 text-lumora-text/60 hover:bg-lumora-secondary/60 hover:text-lumora-dark-wood border border-lumora-secondary'
            }`}
          >
            <span>{tab.icon}</span>
            {tab.label}
            {tabCounts[tab.id] > 0 && (
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id
                    ? 'bg-lumora-white/20'
                    : 'bg-lumora-secondary'
                }`}
              >
                {tabCounts[tab.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <p className="text-red-600 mb-4 px-4 py-3 bg-red-50 rounded-xl border border-red-200">
          {error}
        </p>
      )}

      {/* ── Search / date toolbar (hidden for verification tab — less useful there) ── */}
      {activeTab !== 'verification' && (
        <OrdersToolbar
          search={search}
          onSearchChange={setSearch}
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          sortBy={sortBy}
          onSortChange={setSortBy}
        />
      )}

      {/* ──────────────────────────────────────
          SECTION 1 — Pending Payment Verification
          ────────────────────────────────────── */}
      {activeTab === 'verification' && (
        <PendingVerificationTable
          orders={filtered.length ? filtered : tabOrders}
          onVerify={handleVerify}
          onReject={handleReject}
          actionLoading={actionLoading}
        />
      )}

      {/* ──────────────────────────────────────
          SECTION 2 — Active Orders
          ────────────────────────────────────── */}
      {activeTab === 'active' && (
        <>
          {groups.length === 0 ? (
            <div className="glass-card text-center text-lumora-text/50 py-12">
              No active orders
            </div>
          ) : (
            <OrderMonthGroups
              groups={groups}
              showActions
              onAdvance={handleAdvance}
              nextStatus={nextStatus}
              onCancel={(orderId) => {
                const order = allOrders.find((o) => o.orderId === orderId);
                if (canCancel(order)) setCancelId(orderId);
              }}
              /* Pass canCancel so OrderMonthGroups knows when to show the Cancel btn */
              canCancelOrder={canCancel}
            />
          )}
        </>
      )}

      {/* ──────────────────────────────────────
          SECTION 3 — Completed Orders (read-only)
          ────────────────────────────────────── */}
      {activeTab === 'completed' && (
        <>
          {groups.length === 0 ? (
            <div className="glass-card text-center text-lumora-text/50 py-12">
              No completed orders yet
            </div>
          ) : (
            <OrderMonthGroups groups={groups} showActions={false} />
          )}
        </>
      )}

      {/* ──────────────────────────────────────
          SECTION 4 — Cancelled / Rejected
          ────────────────────────────────────── */}
      {activeTab === 'cancelled' && (
        <>
          {groups.length === 0 ? (
            <div className="glass-card text-center text-lumora-text/50 py-12">
              No cancelled or rejected orders
            </div>
          ) : (
            <OrderMonthGroups groups={groups} showActions={false} />
          )}
        </>
      )}

      {/* ── Export ── */}
      <div className="mt-8">
        <ReportExportBar
          onExport={(params) =>
            runOrdersReportExport({
              ...params,
              orders: tabOrders,
              category: currentTabConfig.label,
              includeRevenueStats: activeTab === 'completed',
            })
          }
        />
      </div>

      {/* ── Cancel confirmation dialog ── */}
      <ConfirmDialog
        open={!!cancelId}
        title="Cancel Order"
        message="Cancel this order and restore product stock? This cannot be undone."
        confirmLabel="Cancel order"
        immediateClose
        onConfirm={handleCancel}
        onCancel={() => setCancelId(null)}
      />
    </div>
  );
}

export { default as OrdersListPage } from './OrdersListPage';
